import { useState, useEffect, useReducer, useRef } from 'react';

// Material UI
import {
    styled,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';

// Components
import FullScreenModal from 'components/popups/FullScreenModal';
import CompForm from 'components/comp/CompForm';
import CompRegistration from 'components/comp/CompRegistration';
import CompReview from 'components/comp/CompReview';
import CompResults from 'components/comp/CompResults';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';

// Utility
import {
    getCompProducts,
    getCompRegistrationForm,
    getSeatChannels,
    createCompOrder,
    releaseHoldToken,
} from 'components/comp/CompAPI';
import {
    isRegistrationRequired,
    initializeAttendee,
    formatRegistrationFormForPurchase,
} from 'components/comp/CompRegistrationUtils';
import { useEvent } from 'context/event';
import { useNotification } from 'context/notification';
import { formatPrice } from 'utility/numbers';
import { normalizeCountryCode } from 'utility/countries';
import moment from 'moment-timezone';

const defaultState = {
    page: 1,
    cart: {
        products: [],
        bundles: [],
        seats: [],
        seatProducts: [],
        channelKeys: [],
        seatPricing: null,
        availableCategories: null,
        status: 'loading', // loading, error, success
    },
    session: {
        expiresAt: null,
        holdToken: null,
        expiredOpen: false,
    },
    customer: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
    },
    order: {
        loading: false,
        orderId: null,
        unipassCount: 0,
    },
    registration: {
        mode: 0,
        questions: [],
        form: [],
    },
};

function initializeState(event) {
    return {
        ...defaultState,
        customer: {
            ...defaultState.customer,
            country: normalizeCountryCode(event.country), // Default country to that of the event
        },
    };
}

function reducer(state, action) {
    switch (action.type) {
        case 'setCart':
            return {
                ...state,
                cart: {
                    ...state.cart,
                    seatProducts: action.seatProducts,
                    channelKeys: action.channelKeys,
                    seatPricing: action.seatPricing,
                    availableCategories: action.availableCategories,
                    products: action.products,
                    bundles: action.bundles,
                    status: 'success',
                },
                registration: action.registration,
            };

        case 'setCartError':
            return { ...state, cart: { ...state.cart, status: 'error' } };

        case 'updateCartProducts':
            const products = state.cart.products.map((i) => {
                if (action.id === i.id) {
                    return { ...i, quantity: action.value };
                } else {
                    return i;
                }
            });

            const p_form = isRegistrationRequired(
                state.registration.mode,
                state.registration.requiredQuestions,
                state.registration.customQuestions,
                products,
                state.cart.bundles,
            )
                ? initializeAttendee(
                      state.registration.mode,
                      products,
                      state.cart.bundles,
                      state.registration.requiredQuestions,
                      state.registration.customQuestions,
                  )
                : [];

            return {
                ...state,
                cart: { ...state.cart, products: products },
                registration: { ...state.registration, form: p_form },
            };

        case 'updateCartBundles':
            const bundles = state.cart.bundles.map((i) => {
                if (action.id === i.id) {
                    return { ...i, quantity: action.value };
                } else {
                    return i;
                }
            });

            const b_form = isRegistrationRequired(
                state.registration.mode,
                state.registration.requiredQuestions,
                state.registration.customQuestions,
                state.cart.products,
                bundles,
            )
                ? initializeAttendee(
                      state.registration.mode,
                      state.cart.products,
                      bundles,
                      state.registration.requiredQuestions,
                      state.registration.customQuestions,
                  )
                : [];

            return {
                ...state,
                cart: { ...state.cart, bundles: bundles },
                registration: { ...state.registration, form: b_form },
            };

        case 'addCartSeat': {
            const prevSeats = state.cart.seats;
            const { seat } = action;

            return { ...state, cart: { ...state.cart, seats: [...prevSeats, seat] } };
        }

        case 'removeCartSeat': {
            const prevSeats = state.cart.seats;
            const { seat } = action;
            let newSeats = [];

            if (seat.object.type === 'seat' || seat.object.type === 'table') {
                newSeats = prevSeats.filter((el) => el.object.uuid !== seat.object.uuid);
            } else if (seat.object.type === 'ga') {
                let removed = false;
                newSeats = prevSeats.filter((el) => {
                    if (
                        removed === false &&
                        el.object.uuid === seat.object.uuid &&
                        el.productId === seat.productId
                    ) {
                        removed = true;
                        return false;
                    } else {
                        return true;
                    }
                });
            }

            return { ...state, cart: { ...state.cart, seats: newSeats } };
        }

        case 'updateCustomer':
            return { ...state, customer: { ...state.customer, ...action.form } };

        case 'updateRegistrationForm':
            return { ...state, registration: { ...state.registration, form: action.form } };

        case 'setSession':
            return {
                ...state,
                session: {
                    ...state.session,
                    expiresAt: action.expiresAt,
                    holdToken: action.holdToken,
                },
            };

        case 'setSessionExpiredOpen':
            return {
                ...state,
                session: {
                    ...state.session,
                    expiredOpen: action.value,
                },
            };

        case 'clearSession':
            return {
                ...state,
                session: {
                    ...state.session,
                    expiresAt: null,
                    holdToken: null,
                },
            };

        case 'setOrderLoading':
            return { ...state, order: { ...state.order, loading: action.value } };

        case 'setOrder':
            return {
                ...state,
                page: 4,
                order: {
                    ...state.order,
                    loading: false,
                    orderId: action.id,
                    unipassCount: action.unipassCount,
                },
            };

        case 'next':
            if (state.page + 1 === 2 && state.registration.form.length === 0) {
                return { ...state, page: state.page + 2 }; // Skip Registration if no questions are avilable to answer
            } else {
                return { ...state, page: state.page + 1 };
            }

        case 'back':
            if (state.page - 1 === 2 && state.registration.form.length === 0) {
                return { ...state, page: state.page - 2 }; // Skip Registration if no questions are avilable to answer
            } else {
                return { ...state, page: state.page - 1 };
            }

        case 'restart':
            return {
                ...defaultState,
                cart: {
                    ...state.cart,
                    products: state.cart.products.map((i) => ({ ...i, quantity: 0 })),
                    bundles: state.cart.bundles.map((i) => ({ ...i, quantity: 0 })),
                    seats: [],
                    status: 'success',
                },
                registration: {
                    ...state.registration,
                    form: [],
                },
                customer: {
                    ...defaultState.customer,
                    country: state.customer.country, // Keep the default country
                },
            };

        case 'defaultState':
            return {
                ...defaultState,
                customer: {
                    ...defaultState.customer,
                    country: state.customer.country, // Keep the default country
                },
            };

        default:
            throw new Error();
    }
}

export default function Comp(props) {
    const { open, onClose, eventId, onOrderCreate } = props;

    const { notify } = useNotification();
    const event = useEvent();
    const timezone = event.time_zone;

    const [state, dispatch] = useReducer(reducer, defaultState, () => initializeState(event));

    let modalTitle;
    if (state.page === 1) modalTitle = 'Create Order';
    if (state.page === 2) modalTitle = 'Checkout Questions'; // Skipped if there are no registration questions to ask
    if (state.page === 3) modalTitle = 'Review Order';
    if (state.page === 4) modalTitle = 'Share Order';

    const eventIdRef = useRef();

    useEffect(() => {
        // Event ID has changed. Reset state for this component.
        if (eventId !== eventIdRef.current) {
            dispatch({ type: 'defaultState' });
            eventIdRef.current = eventId;
        }

        // Load or reload products whenever the modal is opened (or event id changes)
        if (open === true) {
            Promise.all([
                getCompProducts(eventId),
                getCompRegistrationForm(eventId),
                getSeatChannels(eventId),
            ])
                .then((result) => {
                    const [_products, _registration, channelKeysResponse] = result;

                    // *** Assigned Seating *** //

                    let seatProducts = [];
                    let channelKeys = [];
                    let seatPricing = null;
                    let availableCategories = null;

                    if (event.seats_flag) {
                        seatProducts = _products.seatProducts.map((sp) => ({
                            id: sp.product_id,
                            name: sp.prod_name,
                            type: sp.prod_type_id,
                            seatPricing: sp.seat_pricing,
                            isAttendee: Boolean(sp.is_attendee),
                        }));

                        if (Array.isArray(channelKeysResponse)) {
                            channelKeys = channelKeysResponse;
                        }

                        const hasMultiLevel = seatProducts.some(
                            (p) =>
                                p.type === 3 &&
                                p.seatPricing &&
                                Array.isArray(p.seatPricing) &&
                                p.seatPricing.length > 0,
                        );

                        seatPricing = generateSeatPricing(seatProducts, hasMultiLevel);

                        availableCategories = seatPricing.prices.map((p) => p.category);
                    }

                    // *** Products *** //

                    const products = _products.products.map((p) => {
                        let status = 'on_sale';

                        if (moment(p.sale_start_date).tz(timezone, true) > moment())
                            status = 'sale_not_started';
                        if (moment(p.sale_end_date).tz(timezone, true) < moment())
                            status = 'sale_concluded';
                        if (p.qty_left <= 0) status = 'sold_out';
                        if (p.visible === 0) status = 'hidden';

                        return {
                            id: p.product_id,
                            name: p.prod_name,
                            type: p.prod_type_id,
                            status: status,
                            quantity: 0,
                            isAttendee: Boolean(p.is_attendee),
                        };
                    });

                    // *** Bundles *** //

                    const bundles = _products.bundles.map((b) => {
                        let status = 'on_sale';

                        if (moment(b.sale_start_date).tz(timezone, true) > moment())
                            status = 'sale_not_started';
                        if (moment(b.sale_end_date).tz(timezone, true) < moment())
                            status = 'sale_concluded';
                        if (b.qty_left <= 0) status = 'sold_out';
                        if (b.visible === 0) status = 'hidden';

                        return {
                            id: b.bundle_id,
                            name: b.bundle_name,
                            status: status,
                            quantity: 0,
                            products: b.products.map((bp) => ({
                                id: bp.product_id,
                                name: bp.prod_name,
                                quantity: bp.qty,
                                price: bp.price,
                                isAttendee: Boolean(bp.is_attendee),
                            })),
                        };
                    });

                    const registration = {
                        mode: _registration.checkout_question_mode,
                        requiredQuestions: _registration.requiredQuestions.map((q) => ({
                            ...q,
                            required: 0,
                        })), // No questions are required when filled out by the host
                        customQuestions: _registration.customQuestions.map((q) => ({
                            ...q,
                            required: 0,
                        })), // No questions are required when filled out by the host
                        form: [],
                    };

                    dispatch({
                        type: 'setCart',
                        seatProducts,
                        channelKeys,
                        seatPricing,
                        availableCategories,
                        products,
                        bundles,
                        registration,
                    });
                })
                .catch(() => {
                    dispatch({ type: 'setCartError' });
                });
        }
    }, [open, eventId, timezone, event.seats_flag]);

    const handleClose = () => {
        const holdToken = state.session.holdToken;
        if (holdToken) {
            releaseHoldToken(eventId, holdToken).catch(() => {});
        }
        onClose();
        setTimeout(() => {
            dispatch({ type: 'defaultState' });
        }, 500);
    };

    const handleSubmit = () => {
        const {
            firstName,
            lastName,
            email,
            phone,
            company,
            address,
            city,
            province,
            postalCode,
            country,
        } = state.customer;
        const { form } = state.registration;

        dispatch({ type: 'setOrderLoading', value: true });

        createCompOrder(eventId, {
            eventId: eventId,
            customer: {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                phone: phone.trim() || null,
                company: company.trim() || null,
                address: address.trim() || null,
                city: city.trim() || null,
                province: province || null,
                postalCode: postalCode.trim() || null,
                country: country,
            },
            products: state.cart.products.filter((i) => i.quantity > 0),
            bundles: state.cart.bundles.filter((i) => i.quantity > 0),
            seats: state.cart.seats,
            channelKeys: state.cart.channelKeys,
            holdToken: state.session.holdToken,
            registration: formatRegistrationFormForPurchase(form),
        })
            .then((order) => {
                dispatch({
                    type: 'setOrder',
                    id: order.tran_id,
                    unipassCount: order.unipass_count,
                });
                dispatch({ type: 'clearSession' });

                onOrderCreate(); // Notifies <Order /> component that an order was created. Order list will refresh.
            })
            .catch((error) => {
                notify.error(error?.userMessage || 'There was a problem creating your order');
                dispatch({ type: 'setOrderLoading', value: false });
            });
    };

    const showSessionMonitor = Boolean(open && event.seats_flag && [1, 2, 3].includes(state.page));

    const sessionExpired = event.seats_flag === 1 && state.session.holdToken === null;

    return (
        <FullScreenModal open={open} onClose={handleClose} title={modalTitle} cancelText="Cancel">
            {showSessionMonitor && (
                <SessionMonitor
                    expiresAt={state.session.expiresAt}
                    onSessionExpired={() =>
                        dispatch({ type: 'setSessionExpiredOpen', value: true })
                    }
                />
            )}
            {state.cart.status === 'error' && (
                <Box
                    height={'100%'}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    m={4}
                >
                    <Typography textAlign={'center'}>
                        There was a problem setting up your order. Please close the windown and try
                        again.
                    </Typography>
                </Box>
            )}
            {state.cart.status === 'success' && (
                <Root p={[2, 4]}>
                    {open === true && state.page === 1 && (
                        <CompForm
                            eventId={eventId}
                            seatsEnabled={Boolean(event.seats_flag)}
                            customer={state.customer}
                            cart={state.cart}
                            sessionExpired={sessionExpired}
                            onCartProductUpdate={(id, value) =>
                                dispatch({ type: 'updateCartProducts', id, value })
                            }
                            onCartBundleUpdate={(id, value) =>
                                dispatch({ type: 'updateCartBundles', id, value })
                            }
                            onCartSeatAdd={(seat, ticket) =>
                                dispatch({ type: 'addCartSeat', seat, ticket })
                            }
                            onCartSeatRemove={(seat, ticket) =>
                                dispatch({ type: 'removeCartSeat', seat, ticket })
                            }
                            onCustomerUpdate={(form) => dispatch({ type: 'updateCustomer', form })}
                            onSessionInitialized={(session) =>
                                dispatch({
                                    type: 'setSession',
                                    expiresAt: session.expiresAt,
                                    holdToken: session.token,
                                })
                            }
                            onSubmit={() => dispatch({ type: 'next' })}
                            onCancel={handleClose}
                        />
                    )}

                    {open === true && state.page === 2 && (
                        <CompRegistration
                            form={state.registration.form}
                            mode={state.registration.mode}
                            sessionExpired={sessionExpired}
                            onFormChange={(form) =>
                                dispatch({ type: 'updateRegistrationForm', form })
                            }
                            onSubmit={() => dispatch({ type: 'next' })}
                            onCancel={() => dispatch({ type: 'back' })}
                        />
                    )}

                    {open === true && state.page === 3 && (
                        <CompReview
                            customer={state.customer}
                            cart={state.cart}
                            registration={state.registration.form}
                            sessionExpired={sessionExpired}
                            loading={state.order.loading}
                            onCancel={() => dispatch({ type: 'back' })}
                            onSubmit={handleSubmit}
                        />
                    )}

                    {open === true && state.page === 4 && (
                        <CompResults
                            eventId={eventId}
                            orderId={state.order.orderId}
                            multiTicket={state.order.unipassCount > 0}
                            customer={state.customer}
                            products={state.cart.products}
                            bundles={state.cart.bundles}
                            seats={state.cart.seats}
                            registration={state.registration.form}
                            onClose={handleClose}
                            onRestart={() => dispatch({ type: 'restart' })}
                        />
                    )}
                </Root>
            )}
            <Dialog
                open={state.session.expiredOpen}
                onClose={() => dispatch({ type: 'setSessionExpiredOpen', value: false })}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Session expired</DialogTitle>
                <DialogContent>
                    Your session has expired and all seats have been released to the public. Please
                    select your seats again.
                </DialogContent>
                <DialogActions>
                    <CancelButton onClick={handleClose}>Exit</CancelButton>
                    <SubmitButton onClick={() => dispatch({ type: 'restart' })}>
                        Restart
                    </SubmitButton>
                </DialogActions>
            </Dialog>
        </FullScreenModal>
    );
}

function SessionMonitor({ expiresAt, onSessionExpired }) {
    const [time, setTime] = useState(null);

    const onSessionExpiredRef = useRef(onSessionExpired);

    useEffect(() => {
        onSessionExpiredRef.current = onSessionExpired;
    }, [onSessionExpired]);

    // Prevent double-calling if the interval ticks again at 0
    const sessionExpiredRef = useRef(false);

    // Session countdown timer
    useEffect(() => {
        sessionExpiredRef.current = false;
        if (expiresAt !== null) {
            const updateTimer = () => {
                const now = new Date();
                const expiresAtDate = new Date(expiresAt);
                const diffMs = expiresAtDate - now;
                const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
                setTime(formatTimer(diffSeconds));

                if (diffSeconds === 0 && !sessionExpiredRef.current) {
                    sessionExpiredRef.current = true;
                    onSessionExpiredRef.current();
                }
            };

            // Update immediately
            updateTimer();

            // Then update every second
            const interval = setInterval(updateTimer, 1000);

            return () => {
                clearInterval(interval);
            };
        }
    }, [expiresAt]);

    return (
        <SessionView>
            <Typography textAlign={'center'}>{time}</Typography>
        </SessionView>
    );
}

const Root = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    justifyContent: 'center',
    flexGrow: 1,
}));

const SessionView = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    position: 'sticky',
    top: 56, // Below AppBar
    zIndex: theme.zIndex.appBar - 1,
    [theme.breakpoints.up('sm')]: {
        top: 64,
    },
}));

const generateSeatPricing = (seatProducts, hasMultiLevel) => {
    if (hasMultiLevel) {
        // Multi-level pricing mode
        // Group by category, then add ticket types for each category
        const categoryMap = new Map();

        seatProducts.forEach((product) => {
            product.seatPricing?.forEach((sp) => {
                const categoryId = convertCategoryKey(sp.seat_category_id);

                if (!categoryMap.has(categoryId)) {
                    categoryMap.set(categoryId, {
                        category: categoryId,
                        ticketTypes: [],
                    });
                }

                categoryMap.get(categoryId).ticketTypes.push({
                    ticketType: String(product.id),
                    label: product.name,
                    price: sp.price,
                });
            });
        });

        // Convert to pricing array, using simple format for categories with only one ticket type
        // Simple format auto-selects without showing a popup
        const pricing = Array.from(categoryMap.values()).map((cat) => {
            if (cat.ticketTypes.length === 1) {
                // Single ticket type - use simple pricing (no popup)
                return {
                    category: cat.category,
                    ticketType: cat.ticketTypes[0].ticketType,
                    price: cat.ticketTypes[0].price,
                };
            }
            // Multiple ticket types - use ticketTypes array (shows popup)
            return cat;
        });

        return {
            priceFormatter: (price) => formatPrice(price),
            prices: pricing,
        };
    }
    // Single pricing (backwards compatibility)
    const pricingConfig = {
        priceFormatter: (price) => formatPrice(price),
        prices: seatProducts.map((p) => ({
            category: p.name,
            price: p.price,
        })),
    };

    return pricingConfig;
};

// Helper to convert category key to the correct type for seats.io
// SeatsIO lets you set the category key to either a number or string
// whole numbers >= 1 (and no leading zeroes) will be numbers
// anything else is a string for seatsio purposes
// we store category keys as varchar in our DB so they are string by default
const convertCategoryKey = (categoryId) => {
    const categoryKeyRegex = /^[1-9][0-9]*$/;
    if (categoryKeyRegex.test(categoryId)) {
        return Number(categoryId);
    }
    return categoryId;
};

// Format seconds to MM:SS
const formatTimer = (seconds) => {
    if (seconds === null) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};
